import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { DeviceAppointmentProfile } from '@/types/scheduling-api';

export function deviceProductsQueryKey(
  deviceId: string | null | undefined,
): readonly [string, string | null | undefined] {
  return ['device-products', deviceId];
}

/**
 * Live products of one device — its appointment profiles — PROD-2007.
 *
 *   GET /api/Resource/{resourceId}/Schedule/AppointmentProfiles
 *
 * PracticeManagement PR #945 (`ResourceScheduleController`). Live only: the
 * route drops archived profiles unless asked, and the booking command refuses
 * an archived / foreign profile with `ResourceNotSchedulable`, so the picker
 * never offers one. Default product first, then A→Z.
 */
export function useDeviceProducts(deviceId: string | null | undefined) {
  return useQuery<DeviceAppointmentProfile[]>({
    queryKey: deviceProductsQueryKey(deviceId),
    queryFn: async ({ signal }) => {
      if (!deviceId) throw new Error('useDeviceProducts called without deviceId');
      const res = await get<DeviceAppointmentProfile[] | null>(
        `/api/Resource/${encodeURIComponent(deviceId)}/Schedule/AppointmentProfiles`,
        { signal },
      );
      return sortDeviceProducts((res ?? []).filter((p) => !p.isArchived));
    },
    enabled: !!deviceId,
    staleTime: 60_000,
    // The booking panel renders its own "no live products" state.
    meta: { skipGlobalError: true },
  });
}

/** Default first, then name A→Z (case-insensitive). Pure, exported for tests. */
export function sortDeviceProducts(
  products: readonly DeviceAppointmentProfile[],
): DeviceAppointmentProfile[] {
  return [...products].sort((a, b) => {
    if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
    return (a.profileName ?? '').toLowerCase().localeCompare((b.profileName ?? '').toLowerCase());
  });
}

/** The product to preselect: the device's default, else its first live one. */
export function defaultDeviceProduct(
  products: readonly DeviceAppointmentProfile[] | undefined,
): DeviceAppointmentProfile | null {
  if (!products || products.length === 0) return null;
  return products.find((p) => p.isDefault) ?? products[0];
}
